<script setup lang="ts">
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '@/modules/commissioning/stores/deviceSpecificationStore';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import {
  getTerminalGroupingWithErrorsFromCommissionResult,
  getCommissionResultConfigValues,
} from '@/modules/commissioning/utils/error-resolver';
import type { ComponentErrorType } from '@/modules/commissioning/utils/error-constants';
import ComErrorIds from '@/modules/commissioning/components/ComErrorIds.vue';
import ComErrorCtaButtons from '@/modules/commissioning/components/ComErrorCtaButtons.vue';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';

const errorType = 'CTNegativeReversed' as ComponentErrorType;
const props = defineProps<{
  failIdsFromRoute?: string[]; // For debugging purposes only
}>();

const deviceStore = useCommissioningDeviceStore();
const deviceSpecificationStore = useDeviceSpecificationStore();
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);
const connections = deviceSpecificationStore.solshareSystemSpec.connections;

/**
 * Get the fail IDs from the commission result
 */
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const failIds = props.failIdsFromRoute ?? commissionResultFailIds[errorType] ?? [];

/**
 * Get the CTA buttons from the commission result
 */
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');
const ctaButtons = commissionResultCtaButtons[errorType] ?? [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  ERROR_CTA_BUTTONS.RECONFIGURE,
];

/**
 * Get the terminal error grouping from the commission result
 */
const terminalErrorGrouping = getTerminalGroupingWithErrorsFromCommissionResult(
  commissionResult,
  connections,
);
const CTReversedUnits = terminalErrorGrouping[errorType] ?? [];
</script>

<template>
  <ProgressFrame
    pageClass="fullfocus-page"
    titleTight
    withBackArrow
    backButtonClass="error-back-button"
  >
    <template #title><div class="error-title">Reversed CT</div></template>
    <template #subTitle
      ><div class="error-sub-title">How to troubleshoot a reversed CT</div></template
    >

    <div v-if="failIds.length > 0" class="error-fail-ids--wrapper">
      <ComErrorIds :failIds="failIds" unique />
    </div>

    <div class="error-intro">
      <p>
        <strong>Each CT must be</strong>
      </p>
      <ul class="error-bullet-list">
        <li>Clipped with the <b>arrow pointing the correct direction</b></li>
        <li>CT tails terminated correctly at SolShare CT connector</li>
      </ul>

      <p v-if="CTReversedUnits.length > 0">Connection to check:</p>
      <ul>
        <li v-for="(unit, i) in CTReversedUnits" :key="i">{{ unit }}</li>
      </ul>
    </div>

    <div class="error-handle-section">
      <h4>CT Install End</h4>
      <p>Ensure that the arrow is pointing to the load (flat).</p>
      <p>Note the arrow markings are on the base of the CT</p>

      <p v-if="CTReversedUnits.length > 0">Connection to check:</p>
      <ul>
        <li v-for="(unit, i) in CTReversedUnits" :key="i">{{ unit }}</li>
      </ul>
    </div>

    <div class="error-handle-section">
      <h4>SolShare End</h4>
      <p>
        CT connector check the polarity of the CT cables for each of the connections listed below.
      </p>
      <p>Note: If extended, the polarity at the point of extension must also be checked.</p>

      <p v-if="CTReversedUnits.length > 0">Connection to check:</p>
      <ul>
        <li v-for="(unit, i) in CTReversedUnits" :key="i">{{ unit }}</li>
      </ul>
    </div>

    <ComErrorCtaButtons :ctaButtons="ctaButtons" />
  </ProgressFrame>
</template>

<style lang="less" scoped></style>
